import { useRef } from 'react';

const haversineDistance = (mk1, mk2) => {
    const R = 6371; // Radius of the Earth in km
    const rlat1 = mk1.lat * (Math.PI / 180); // Convert degrees to radians
    const rlat2 = mk2.lat * (Math.PI / 180); // Convert degrees to radians
    const difflat = rlat2 - rlat1; // Radian difference (latitudes)
    const difflon = (mk2.lng - mk1.lng) * (Math.PI / 180); // Radian difference (longitudes)

    const d = 2 * R * Math.asin(Math.sqrt(Math.sin(difflat / 2) * Math.sin(difflat / 2) + Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflon / 2) * Math.sin(difflon / 2)));
    return d;
};

const MapStore = function MapStore() {
    const store = {};
    let mapHooked = false;
    let timeoutId = null;

    const unhookedWarning = () => {
        if (process.env.NODE_ENV !== 'production' && !timeoutId && typeof window !== 'undefined') {
            timeoutId = setTimeout(() => {
                timeoutId = null;

                if (!mapHooked) {
                    // eslint-disable-next-line no-console
                    console.warn('Instance created by `useMap` is not connected to any Map element. Have you forgotten to pass the `map` prop?');
                }
            });
        }
    };

    // Internal functions
    const setMaps = (map, maps) => {
        store.map = map;
        store.maps = maps;
    };

    const setMarkers = (markers) => {
        store.markers = markers;
    };

    const setCircleAreas = (circles) => {
        store.circles = circles;
    };

    const setClusterer = (clusterer) => {
        store.clusterer = clusterer;
    };

    const getInternalHooks = (key) => {
        if (key === 'INTERNAL_HOOK_KEY') {
            mapHooked = true;

            return {
                setMaps,
                setMarkers,
                setCircleAreas,
                setClusterer,
            };
        }
        // eslint-disable-next-line no-console
        console.warn('`getInternalHooks` is for internal usage only. It should not be called directly.');
        return null;
    };

    // Public functions
    const findNearestMarker = (location) => {
        unhookedWarning();

        if (!store.markers) {
            return null;
        }

        let minDist = Infinity;
        let nearestMarker = null;

        store.markers.forEach(marker => {
            const lat = marker.position.lat();
            const lng = marker.position.lng();
            const dist = haversineDistance(location, { lat, lng });

            if (dist < minDist) {
                minDist = dist;
                nearestMarker = marker;
            }
        });

        return nearestMarker;
    };

    const getCircleAreas = () => {
        unhookedWarning();


        if (store.circles) {
            return store.circles;
        }
        return null;
    };

    const getClusterer = () => {
        if (store.clusterer) {
            return store.clusterer;
        }

        return null;
    };

    const getMaps = () => {
        if (store.map && store.maps) {
            return { map: store.map, maps: store.maps };
        }
        return null;
    };

    const getMarkers = () => {
        unhookedWarning();

        if (store.markers) {
            return store.markers;
        }
        return null;
    };
    const setCenter = (location) => {
        unhookedWarning();

        if (store.map) {
            store.map.setCenter(location);
        }
    };

    const setZoom = (zoom) => {
        unhookedWarning();

        if (store.map) {
            store.map.setZoom(zoom);
        }
    };

    this.getMap = () => ({
        findNearestMarker,
        getCircleAreas,
        getClusterer,
        getMaps,
        getMarkers,
        setCenter,
        setZoom,

        getInternalHooks,
    });
};

function useMap(map) {
    const mapRef = useRef();

    if (!mapRef.current) {
        if (map) {
            mapRef.current = map;
        } else {
            const mapStore = new MapStore();
            mapRef.current = mapStore.getMap();
        }
    }

    return [mapRef.current];
}

export default useMap;
